import { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { formatDateTime } from "../../utils/datetime";
import { useDialog } from "../../components/DialogProvider";
import { CreateNoteModal } from "../../components/CreateNoteModal";
import { Plus, CheckCircle2, AlertCircle, Clock, Loader2 } from "lucide-react";

export default function AccountantNotes() {
  const { toast } = useDialog();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/accountant/notes?status=${statusFilter}` : "/accountant/notes";
      const res = await api.get(url);
      setData(res.data.data || []);
    } catch (err) {
      toast({ type: "error", title: "Error", message: "Gagal memuat catatan." });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleUpdateStatus = async (id, currentStatus) => {
    const nextStatus = currentStatus === "open" ? "in_review" : currentStatus === "in_review" ? "resolved" : "open";
    
    setUpdatingId(id);
    try {
      await api.patch(`/accountant/notes/${id}/status`, { status: nextStatus });
      toast({ type: "success", title: "Sukses", message: `Status diperbarui menjadi ${nextStatus.replace('_', ' ')}` });
      fetchNotes();
    } catch (err) {
      toast({ type: "error", title: "Gagal", message: err.response?.data?.message || "Gagal mengupdate status." });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Catatan & Masalah</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Daftar catatan uang kurang/berlebih, stok hilang, dan investigasi internal lainnya.
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900"
        >
          <Plus className="h-4 w-4" />
          Buat Catatan Baru
        </button>
      </header>

      {/* Filters */}
      <div className="flex gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-[13px] font-medium text-zinc-700 shadow-sm focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
        >
          <option value="">Semua Status</option>
          <option value="open">Status: Open</option>
          <option value="in_review">Status: Ditinjau</option>
          <option value="resolved">Status: Selesai</option>
        </select>
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-[13px]">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
              <tr>
                <th className="px-4 py-3 font-medium">Informasi Utama</th>
                <th className="px-4 py-3 font-medium">Isi Laporan</th>
                <th className="px-4 py-3 font-medium">Referensi</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-zinc-500">Memuat catatan...</td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-zinc-500">Belum ada catatan/temuan tersimpan.</td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id} className="hover:bg-zinc-50/50 transition-colors bg-white">
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-zinc-900 capitalize">{row.category.replace('_', ' ')}</div>
                      <div className="text-[11px] text-zinc-500 mt-1">Pelapor: {row.created_by_name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{formatDateTime(row.created_at)}</div>
                      <span className={`mt-2 inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ${
                        row.priority === 'high' ? 'bg-rose-50 text-rose-700 ring-rose-600/20' :
                        row.priority === 'medium' ? 'bg-amber-50 text-amber-700 ring-amber-600/20' :
                        'bg-zinc-50 text-zinc-700 ring-zinc-500/20'
                      }`}>
                        Prioritas {row.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top min-w-[300px] whitespace-pre-wrap text-zinc-700 leading-relaxed">
                      {row.note}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-700 uppercase tracking-wide ring-1 ring-inset ring-indigo-600/20">
                        {row.reference_type} {row.reference_id ? `#${row.reference_id}` : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {row.status === 'resolved' ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                            <CheckCircle2 className="h-3 w-3" /> Selesai
                          </span>
                          {row.resolved_by_name && <span className="text-[10px] text-zinc-400">oleh {row.resolved_by_name}</span>}
                        </div>
                      ) : row.status === 'in_review' ? (
                        <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                          <Clock className="h-3 w-3" /> Ditinjau
                        </span>
                      ) : (
                        <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 ring-1 ring-inset ring-rose-600/20">
                          <AlertCircle className="h-3 w-3" /> Open
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <button
                        disabled={updatingId === row.id}
                        onClick={() => handleUpdateStatus(row.id, row.status)}
                        className="inline-flex items-center justify-center rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors disabled:opacity-50"
                      >
                        {updatingId === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 
                          row.status === 'open' ? 'Ubah Ditinjau' : 
                          row.status === 'in_review' ? 'Tandai Selesai' : 
                          'Buka Kembali'
                        }
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateNoteModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchNotes}
      />
    </div>
  );
}
